import { computed, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useLayoutStore } from '@/stores/layoutStore';
import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
import { getChart } from '@/composables/getChart';
import { getChartStyle } from '@/composables/getChartStyle';
import { getSharpCharts2ChartStyle } from '@/composables/getSharpCharts2ChartStyle';
import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
import { extractChartStyleObject } from '@/composables/extractChartStyleObject';
import { areChartStyleObjectsEqual } from '@/composables/areChartStyleObjectsEqual';
import { getStyleCacheSettingsOverrides } from '@/utils/getStyleCacheSettingsOverrides';
import { SizeCacheMap } from '@/utils/cache-map';
import { ChartStyle } from '@/interfaces/chart';

const MAX_NUM_CACHE_ENTRIES = 1000;
const ONE_SECOND = 1000;
const REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY = 10 * ONE_SECOND;
const chartStyleJsonCache = new SizeCacheMap<ChartStyle>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, MAX_NUM_CACHE_ENTRIES);

let xmlChartStyleJsonBeingViewed: ComputedRef<ChartStyle | null> | undefined;
let chartStyleJsonBeingViewed: ComputedRef<ChartStyle | null> | undefined;
let chartStyleJsonOfStyleCacheIdOfChartBeingViewed: ComputedRef<ChartStyle | null> | undefined;
let chartStyleJsonOfStyleCacheIdOfChartStyleBeingViewed: ComputedRef<ChartStyle | null> | undefined;
let chartStyleJsonOfChartBeingViewed: ComputedRef<ChartStyle | null> | undefined;
let chartStyleJsonOfChartStyleBeingViewed: ComputedRef<ChartStyle | null> | undefined;

chartStyleJsonCache.onBeforeDeletion(cache => {
  const layoutStore = useLayoutStore();
  const { styleCacheIdOfChartBeingViewed, styleCacheIdOfChartStyleBeingViewed } = useWorkbenchSelections();

  cache.refreshTimestampForKey(styleCacheIdOfChartBeingViewed.value);
  cache.refreshTimestampForKey(styleCacheIdOfChartStyleBeingViewed.value);
  cache.refreshTimestampForKey(layoutStore.i);
});

function addEntryToChartStyleJsonCache(styleCacheId: string, chartStyleJson: ChartStyle): void {
  chartStyleJsonCache.set(styleCacheId, extractChartStyleObject(chartStyleJson));
}

function getChartStyleJsonFromCache(styleCacheId: string): ChartStyle | null {
  if (typeof styleCacheId !== 'string' || !styleCacheId.startsWith('p')) { // TODO: typeof check left in there for now until TS gets better implemented
    return null;
  }

  const chartStyleJsonCacheEntry = chartStyleJsonCache.get(styleCacheId, true);

  if (!chartStyleJsonCacheEntry) {
    return null;
  }

  return extractChartStyleObject(chartStyleJsonCacheEntry);
}

function getPermanentStyleCacheIdFromCache(chartStyleJson: ChartStyle): string {
  const {
    styleCacheIdOfChartBeingViewed,
    styleCacheIdOfChartStyleBeingViewed
  } = useWorkbenchSelections();

  const chartStyleJsonOfChartBeingViewed = chartStyleJsonCache.get(styleCacheIdOfChartBeingViewed.value, false);

  if (
    chartStyleJsonOfChartBeingViewed &&
    styleCacheIdOfChartBeingViewed.value.startsWith('p') &&
    areChartStyleObjectsEqual(chartStyleJson, chartStyleJsonOfChartBeingViewed)
  ) {
    chartStyleJsonCache.refreshTimestampForKey(styleCacheIdOfChartBeingViewed.value);

    return styleCacheIdOfChartBeingViewed.value;
  }

  const chartStyleJsonOfChartStyleBeingViewed = chartStyleJsonCache.get(styleCacheIdOfChartStyleBeingViewed.value, false);

  if (
    chartStyleJsonOfChartStyleBeingViewed &&
    styleCacheIdOfChartStyleBeingViewed.value.startsWith('p') &&
    areChartStyleObjectsEqual(chartStyleJson, chartStyleJsonOfChartStyleBeingViewed)
  ) {
    chartStyleJsonCache.refreshTimestampForKey(styleCacheIdOfChartStyleBeingViewed.value);

    return styleCacheIdOfChartStyleBeingViewed.value;
  }

  const styleCacheIds = chartStyleJsonCache.getAllMatchingKeys(chartStyleJson, areChartStyleObjectsEqual);

  for (const styleCacheId of styleCacheIds) {
    if (styleCacheId.startsWith('p')) {
      chartStyleJsonCache.refreshTimestampForKey(styleCacheId);

      return styleCacheId;
    }
  }

  return '';
}

async function populateChartStyleJsonCacheForChart(recid: number): Promise<void> {
  if (!isValidChartRecid(recid)) {
    return;
  }

  const chart = await getChart(recid);

  if (!chart) {
    window.sc3WorkbenchLogger.error(`Error occurred while populating chartStyleJson for chart with recid: ${recid} - no such chart found.`);
    return;
  }

  await populateChartStyleJsonCacheForStyleCacheId(chart.styleCacheId);
}

async function populateChartStyleJsonCacheForChartStyle(recid: number): Promise<void> {
  if (!isValidChartStyleRecid(recid, true)) {
    return;
  }

  const chartStyle = await getChartStyle(recid);

  if (!chartStyle) {
    window.sc3WorkbenchLogger.error(`Error occurred while populating chartStyleJson for ChartStyle with recid: ${recid} - no such ChartStyle found.`);
    return;
  }

  await populateChartStyleJsonCacheForStyleCacheId(chartStyle.styleCacheId);
}

async function populateChartStyleJsonCacheForStyleCacheId(styleCacheId: string): Promise<void> {
  try {
    await getSharpCharts2ChartStyle(styleCacheId);
  } catch (e) {
    window.sc3WorkbenchLogger.info(`Error occurred while populating chartStyleJson for styleCacheId: ${styleCacheId}.`, e);
  }
}

function registerChartStyleJsonCacheUpdateCallback(callback: () => any): void {
  chartStyleJsonCache.onAfterUpdate(callback);
}

export function useChartStyleJsonCache() {
  const layoutStore = useLayoutStore();
  const {
    styleCacheIdOfChartBeingViewed,
    styleCacheIdOfChartStyleBeingViewed,
    styleCacheSettingsOverridesOfChartBeingViewed,
    styleCacheSettingsOverridesOfChartStyleBeingViewed,
  } = useWorkbenchSelections();

  xmlChartStyleJsonBeingViewed = xmlChartStyleJsonBeingViewed ?? chartStyleJsonCache.getComputedRefValueFromRefKey(storeToRefs(layoutStore).i);

  chartStyleJsonBeingViewed = chartStyleJsonBeingViewed ?? computed(() => {
    if (!xmlChartStyleJsonBeingViewed?.value) {
      return null;
    }

    return {
      ...xmlChartStyleJsonBeingViewed.value,
      ...getStyleCacheSettingsOverrides(layoutStore.chartImageUrlString)
    };
  });

  chartStyleJsonOfStyleCacheIdOfChartBeingViewed = chartStyleJsonOfStyleCacheIdOfChartBeingViewed ?? chartStyleJsonCache.getComputedRefValueFromRefKey(styleCacheIdOfChartBeingViewed);

  chartStyleJsonOfStyleCacheIdOfChartStyleBeingViewed = chartStyleJsonOfStyleCacheIdOfChartStyleBeingViewed ?? chartStyleJsonCache.getComputedRefValueFromRefKey(styleCacheIdOfChartStyleBeingViewed);

  chartStyleJsonOfChartBeingViewed = chartStyleJsonOfChartBeingViewed ?? computed(() => {
    if (!chartStyleJsonOfStyleCacheIdOfChartBeingViewed?.value) {
      return null;
    }

    return {
      ...chartStyleJsonOfStyleCacheIdOfChartBeingViewed.value,
      ...styleCacheSettingsOverridesOfChartBeingViewed.value
    };
  });

  chartStyleJsonOfChartStyleBeingViewed = chartStyleJsonOfChartStyleBeingViewed ?? computed(() => {
    if (!chartStyleJsonOfStyleCacheIdOfChartStyleBeingViewed?.value) {
      return null;
    }

    return {
      ...chartStyleJsonOfStyleCacheIdOfChartStyleBeingViewed.value,
      ...styleCacheSettingsOverridesOfChartStyleBeingViewed.value
    };
  });

  return {
    addEntryToChartStyleJsonCache,
    getChartStyleJsonFromCache,
    getPermanentStyleCacheIdFromCache,
    populateChartStyleJsonCacheForChart,
    populateChartStyleJsonCacheForChartStyle,
    registerChartStyleJsonCacheUpdateCallback,
    chartStyleJsonBeingViewed,
    chartStyleJsonOfChartBeingViewed,
    chartStyleJsonOfChartStyleBeingViewed
  };
}
